import * as React from 'react';
import styled from 'styled-components';
import ShiftAppearance from '@sdk/shiftAppearance/ShiftAppearance';
import Text14 from '@sdk/components/ui/typography/Text14';
import Picker from '@sdk/components/forms/Picker';
import gettext from '@sdk/lang/gettext';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import Row from '@sdk/components/ui/spacing/Row';
import useAuth from '@sdk/authentication/useAuth';
import useConfig from '@sdk/config/useConfig';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Feature } from '@sdk/common/features';
import { LangType } from '@sdk/lang/types/LangType';
import type { SaveClientFlags } from '@sdk/authentication/types';
import type { ShiftAppearanceSettingType } from '@sdk/shiftAppearance/types/ShiftAppearanceType';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';

import ICalPreferencesBanner from '@web/settings/components/ICalPreferencesBanner/ICalPreferencesBanner';

import type { $rootScopeType } from '../common/types/AngularTypes';
import PremiumGuard from '../premiumGuard/PremiumGuard';
import PremiumGuardLabel from '../premiumGuard/PremiumGuardLabel';
import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';
import { languageOptions } from '../../lang/constants';

import type { GoogleCalendarUtilsType } from './types';
import SettingsSection from './components/SettingsSection';
import SettingsHeader from './components/SettingsHeader';
import RowSpaceBetween from './components/RowSpaceBetween';
import SettingsSwitcherRow from './components/SettingsSwitcherRow';
import GoogleCalendarSync from './components/GoogleCalendarSync';
import StagingSection from './components/StagingSection';
import { TIME_FORMATS, NAME_FORMATS, DEFAULT_SETTINGS_PADDING, FIELD_WIDTH_205 } from './constants';
import { getNumberFromBoolean } from './helpers/utils';
import { useUserSettings } from './UserSettingsContext';
import { useSettingsClientFlags } from './SettingsClientFlagsContext';

const RowWithLabel = styled(Row)`
  align-items: center;
`;

type Props = {
  personaCollection: any;
  saveClientFlags: SaveClientFlags;
  googleCalendarUtils: GoogleCalendarUtilsType;
  sectionsConfig: Sections;
  $rootScope: $rootScopeType;
  isWhitelisted: boolean;
};

const PreferencesScreen: React.FC<React.PropsWithChildren<Props>> = ({
  personaCollection,
  googleCalendarUtils,
  sectionsConfig,
  $rootScope,
  isWhitelisted,
}) => {
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const { getPremiumGuardTooltipContent, getRequiredPlan, getPremiumGuardConfig, tooltipRefs } =
    usePremiumGuard();
  const { FEATURE_FLAGS } = useConfig();
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { clientFlags, onClientFlagsChange } = useSettingsClientFlags();
  const isChristmasTheme = window._IS_CHRISTMAS_THEME;

  const isScheduleAutosyncFeatureEnabled = (): boolean => {
    return Array.isArray(FEATURE_FLAGS.DISABLING_SCHEDULE_AUTOSYNC)
      ? FEATURE_FLAGS.DISABLING_SCHEDULE_AUTOSYNC.includes(currentUser.org.id)
      : FEATURE_FLAGS.DISABLING_SCHEDULE_AUTOSYNC;
  };

  const orgCreatedDate = new Date(currentUser.org.createdAt);
  const ICalBanner =
    orgCreatedDate < FEATURE_FLAGS.ICAL_RELEASE_DATE ? <ICalPreferencesBanner /> : null;

  return (
    <>
      <SettingsHeader ref={sectionsConfig.refs.display} title={gettext('Display preferences')} />
      {FEATURE_FLAGS.I18N && (
        <SettingsSection withSeparator>
          <RowSpaceBetween>
            <Text14 semiBold themeColor={ThemeColorType.gray}>
              {gettext('Select language')}
            </Text14>
            <Picker
              options={languageOptions}
              customWidth={FIELD_WIDTH_205}
              value={userSettings.language}
              onChange={(value: LangType): void => onUserSettingsChange({ language: value })}
            />
          </RowSpaceBetween>
        </SettingsSection>
      )}

      <SettingsSection withSeparator>
        <RowSpaceBetween>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Time format')}
          </Text14>
          <Picker
            options={TIME_FORMATS}
            customWidth={FIELD_WIDTH_205}
            value={getNumberFromBoolean(userSettings.usTimeFormat)}
            onChange={(value: number): void =>
              onUserSettingsChange({ usTimeFormat: Boolean(value) })
            }
          />
        </RowSpaceBetween>
      </SettingsSection>

      <SettingsSection withSeparator>
        <RowSpaceBetween>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Name format')}
          </Text14>
          <Picker
            options={NAME_FORMATS}
            customWidth={FIELD_WIDTH_205}
            value={getNumberFromBoolean(userSettings.showLastNameFirst)}
            onChange={(value: number): void =>
              onUserSettingsChange({ showLastNameFirst: Boolean(value) })
            }
          />
        </RowSpaceBetween>
      </SettingsSection>

      <SettingsSwitcherRow
        withSeparator
        value={userSettings.worldClockEnabled}
        title={gettext('World clock')}
        subtitle={gettext(`Display calendar events and schedule according to your local time (${currentUser.timezone})
        When disabled, you see and schedule events according to their location's time zone.`)}
        onChange={(value): void => onUserSettingsChange({ worldClockEnabled: value })}
      />

      {isScheduleAutosyncFeatureEnabled() ? (
        <SettingsSwitcherRow
          withSeparator
          value={userSettings.scheduleAutoSync}
          title={gettext('Schedule auto-sync')}
          subtitle={gettext(
            'Automatically update the schedule if other users in your company are making changes.'
          )}
          onChange={(value): void => onUserSettingsChange({ scheduleAutoSync: value })}
        />
      ) : null}

      <PremiumGuard
        hasAccess={currentUser.org.isPremium}
        premiumGuardModalName={PremiumModalNames.shiftAppearance}
        tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.shiftAppearance)]}>
        <SettingsSection pBottom={DEFAULT_SETTINGS_PADDING}>
          <RowWithLabel>
            <Text14 semiBold themeColor={ThemeColorType.gray}>
              {gettext('Shift appearance on calendar')}
            </Text14>
            {!currentUser.org.isPremium && (
              <PremiumGuardLabel
                label={getRequiredPlan()}
                tooltipId={getPremiumTooltipId(Feature.shiftAppearance)}
                tooltipText={getPremiumGuardTooltipContent(Feature.shiftAppearance)}
                pLeft={10}
              />
            )}
          </RowWithLabel>
          <Text14 themeColor={ThemeColorType.gray} pBottom={20}>
            {gettext(
              'There are three possible versions for shift display. Day view, week view, and month view. The month view also dictates the appearance of shifts when sorting by start time in week view. Drag elements from the left side onto the desired position on the shift block preview on the right.'
            )}
          </Text14>
          <ShiftAppearance
            locations={personaCollection.locations}
            positions={personaCollection.positions}
            tags={personaCollection.tags}
            value={userSettings.shiftAppearance}
            onChange={(value: ShiftAppearanceSettingType): void =>
              onUserSettingsChange({ shiftAppearance: value })
            }
          />
        </SettingsSection>
      </PremiumGuard>

      <SettingsHeader
        ref={sectionsConfig.refs.other}
        withSeparator
        title={gettext('Other preferences')}
      />
      <SettingsSwitcherRow
        withSeparator
        value={userSettings.playSound}
        onChange={(value): void => onUserSettingsChange({ playSound: value })}
        title={gettext('Sound effects')}
        subtitle={gettext('Enable sound effects for sent and received messages.')}
      />

      <SettingsSwitcherRow
        withSeparator
        value={userSettings.showTyping}
        onChange={(value): void => onUserSettingsChange({ showTyping: value })}
        title={gettext('Show others when I am typing')}
        subtitle={gettext('Allow others to see when you are typing a message.')}
      />

      {isChristmasTheme && (
        <SettingsSwitcherRow
          withSeparator
          value={clientFlags.disableChristmasTheme}
          onChange={(value) => {
            onClientFlagsChange({ disableChristmasTheme: value });
          }}
          title={gettext('Disable Christmas theme')}
          subtitle={gettext(
            'Sling uses special theme during Christmas season. Turn this option on to ignore it and stick to regular theme'
          )}
        />
      )}

      {FEATURE_FLAGS.ICAL_RELEASE_DATE ? (
        ICalBanner
      ) : (
        <PremiumGuard
          premiumFeature={Feature.googleCalendarSync}
          premiumGuardModalName={PremiumModalNames.googleCalendarSync}
          hasAccess={isOrgFeatureEnabled(Feature.googleCalendarSync)}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.googleCalendarSync)]}>
          <GoogleCalendarSync
            premiumGuard={getPremiumGuardConfig(Feature.googleCalendarSync)}
            googleCalendarUtils={googleCalendarUtils}
          />
        </PremiumGuard>
      )}
      <Tooltip />

      {__STAGING__ || isWhitelisted ? <StagingSection $rootScope={$rootScope} /> : null}
    </>
  );
};

export default PreferencesScreen;
